import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Wifi, Shield, Zap, Clock, Users, Award, CheckCircle2, TrendingUp, Phone, Mail, MapPin } from 'lucide-react';

const AboutUs = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Wifi className="h-8 w-8" />,
      title: "High-Speed Fiber Internet",
      description: "Lightning-fast fiber optic connections up to 1 Gbps for seamless streaming, gaming, and work from home."
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Security Solutions",
      description: "Advanced network security and firewall protection to keep your data safe and secure."
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Reliable Connectivity",
      description: "99.9% uptime guarantee with redundant infrastructure and 24/7 network monitoring."
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: "24/7 Support",
      description: "Round-the-clock technical support and customer service for all your connectivity needs."
    }
  ];

  const values = [
    {
      icon: <Users className="h-8 w-8" />,
      title: "Customer First",
      description: "We prioritize customer satisfaction above all else, ensuring personalized service and support."
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: "Excellence",
      description: "Committed to delivering the highest quality internet services with cutting-edge technology."
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Innovation",
      description: "Continuously upgrading our infrastructure to provide faster and more reliable connections."
    },
    {
      icon: <CheckCircle2 className="h-8 w-8" />,
      title: "Reliability",
      description: "Trusted by thousands of homes and businesses for consistent, dependable internet service."
    }
  ];

  const features = [
    "No data caps or throttling",
    "Free installation and setup",
    "Flexible plans for every budget",
    "Local customer service team",
    "Business-grade solutions",
    "Scalable bandwidth options"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-pink-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-yellow-500 to-pink-600 opacity-90"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljfGVufDB8fHx8MTc2MjIwNDE1Mnww&ixlib=rb-4.1.0&q=85')",
            opacity: 0.2
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About Netix Fibernet
          </h1>
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto">
            Connecting India with lightning-fast fiber optic internet and unparalleled service since day one.
          </p>
          <Button
            onClick={() => navigate('/signup')}
            className="h-14 px-8 text-lg font-bold bg-white text-red-600 hover:bg-gray-100 shadow-2xl"
          >
            Join Our Network Today
          </Button>
        </div>
      </div>

      {/* Company Overview */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Who We Are
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              Netix Fibernet is a leading internet service provider dedicated to bringing high-speed, reliable fiber optic internet to homes and businesses across India. We believe that everyone deserves access to fast, affordable, and dependable internet connectivity.
            </p>
            <p className="text-lg text-gray-700 mb-4">
              With state-of-the-art infrastructure and a customer-centric approach, we've built a network that powers thousands of connections, enabling seamless streaming, gaming, remote work, and digital experiences.
            </p>
            <p className="text-lg text-gray-700">
              Our mission is simple: <strong>Connect communities, empower businesses, and deliver exceptional internet experiences that transform lives.</strong>
            </p>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG9mZmljZXxlbnwwfHx8fDE3NjIyMDQxNjN8MA&ixlib=rb-4.1.0&q=85"
              alt="Netix Team"
              className="rounded-2xl shadow-2xl border-4 border-red-300"
            />
            <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-red-600 to-pink-600 text-white p-6 rounded-xl shadow-xl">
              <p className="text-3xl font-bold">1000+</p>
              <p className="text-sm">Happy Customers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Services */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Our Services
            </h2>
            <p className="text-xl text-gray-600">Comprehensive solutions for all your connectivity needs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {services.map((service, index) => (
              <Card key={index} className="border-4 border-red-200 hover:border-yellow-400 transition-all duration-300 shadow-lg hover:shadow-2xl bg-white/90 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                    style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}>
                    <div className="text-white">
                      {service.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-yellow-300">
            <img
              src="https://images.unsplash.com/photo-1606814540563-5c02d62fd409?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxmaWJlciUyMG9wdGljfGVufDB8fHx8MTc2MjIwNDE1Mnww&ixlib=rb-4.1.0&q=85"
              alt="Fiber Network"
              className="w-full h-64 object-cover"
            />
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-16 bg-gradient-to-br from-red-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxmaWJlciUyMG9wdGljfGVufDB8fHx8MTc2MjIwNDE1Mnww&ixlib=rb-4.1.0&q=85"
                alt="Network Infrastructure"
                className="rounded-2xl shadow-2xl border-4 border-pink-300"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-6" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Why Choose Netix?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                We're not just another internet service provider. We're your technology partner, committed to delivering excellence in every connection.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-md border-2 border-red-200">
                    <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Values & Culture */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Our Values & Culture
            </h2>
            <p className="text-xl text-gray-600">The principles that drive everything we do</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {values.map((value, index) => (
              <Card key={index} className="border-4 border-pink-200 hover:border-red-400 transition-all duration-300 shadow-lg hover:shadow-2xl bg-gradient-to-br from-white to-pink-50">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto"
                    style={{ background: 'linear-gradient(135deg, #ec4899 0%, #dc2626 100%)' }}>
                    <div className="text-white">
                      {value.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-red-300">
            <img
              src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG9mZmljZXxlbnwwfHx8fDE3NjIyMDQxNjN8MA&ixlib=rb-4.1.0&q=85"
              alt="Team Collaboration"
              className="w-full h-64 object-cover"
            />
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-r from-red-600 via-yellow-500 to-pink-600 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <p className="text-5xl font-bold mb-2">1000+</p>
              <p className="text-lg">Active Users</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">99.9%</p>
              <p className="text-lg">Uptime</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">1 Gbps</p>
              <p className="text-lg">Max Speed</p>
            </div>
            <div>
              <p className="text-5xl font-bold mb-2">24/7</p>
              <p className="text-lg">Support</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA Section */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Get Connected Today
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Ready to experience the fastest, most reliable internet in your area? Join thousands of satisfied customers who trust Netix Fibernet.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-yellow-500 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Call Us</p>
                    <a href="tel:+919319919988" data-testid="about-support-mobile-link" className="text-red-600 font-bold text-lg hover:underline block">
                      +91 93199 19988
                    </a>
                    <a href="tel:06276459015" data-testid="about-office-phone-link" className="text-gray-700 font-semibold hover:text-red-600 hover:underline">
                      Office Phone - 06276 - 459015
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-pink-600 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Email Us</p>
                    <a href="mailto:support@netixfibernet.com" className="text-red-600 font-bold hover:underline">
                      support@netixfibernet.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-600 to-red-600 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Visit Us</p>
                    <p className="text-gray-700">Serving communities across India</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/signup')}
                className="h-14 px-8 text-lg font-bold text-white border-2 border-yellow-400 shadow-lg hover:shadow-xl"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
              >
                Start Your Journey With Us
              </Button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1489436969537-cf0c1dc69cba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxpbnRlcm5ldCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzYyMjA0MTU3fDA&ixlib=rb-4.1.0&q=85"
                alt="Contact Us"
                className="rounded-2xl shadow-2xl border-4 border-yellow-300"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
